<script lang="ts" setup>
	import type {
		CalcObject,
		CargosDetailed,
		CalcPayload,
		CalcItem as ICalcItem,
		ClearanceItem as IClearanceItem,
	} from '~/types';

	interface CalcProps {
		id?: string;
	}

	const runtimeConfig = useRuntimeConfig();
	const toast = useToast();

	const emit = defineEmits(['calcResult']);

	defineProps<CalcProps>();

	const results = ref<CalcObject>();
	const autoRes = ref<CalcObject>();
	const trainRes = ref<CalcObject>();

	const items = ref<ICalcItem[]>([
		{
			height: undefined,
			width: undefined,
			length: undefined,
			weight: undefined,
			count: 1,
			goods: [],
		},
	]);

	const orderPayload = ref();

	const files = ref<{ url: string; file: File }[]>([]);

	const isClearance = ref(false);
	const isInsure = ref(false);
	const isActiveModal = ref(false);

	const blockOfFormDimensions = useTemplateRef('blockOfFormDimensions');
	// const blockOfFormCargoGoods = useTemplateRef('blockOfFormCargoGoods');

	const calcStorage = useCalculatorStorage();

	const isDelete = computed(() => items.value.length !== 1);

	watchEffect(() => {
		emit('calcResult', {
			avia: results.value,
			auto: autoRes.value,
			train: trainRes.value,
		});
	});

	const checkDimensionsError = () => {
		let hasErrors = false;
		blockOfFormDimensions.value?.forEach((el) => {
			el?.validate();
			if (!el || !el.meta.valid) {
				hasErrors = true;
			}
		});
		// blockOfFormCargoGoods.value?.forEach((el) => {
		// 	if (!el || el.checkError()) {
		// 		hasErrors = true;
		// 	}
		// });
		return hasErrors;
	};

	const addItem = () => {
		const hasError = checkDimensionsError();
		if (hasError) {
			return;
		}

		const item: ICalcItem = {
			height: undefined,
			width: undefined,
			length: undefined,
			weight: undefined,
			count: 1,
			goods: [],
		};

		items.value.push(item);
	};

	const addGoods = (itemIdx: number) => {
		const good: IClearanceItem = {
			category: undefined,
			currency: 'cny',
			name: undefined,
			price: undefined,
		};

		if (Array.isArray(items.value[itemIdx].goods)) {
			items.value[itemIdx].goods?.push(good);
		} else {
			items.value[itemIdx].goods = [good];
		}
	};

	// watch(isClearance, (newV, oldV) => {
	// 	if (newV && !oldV) {
	// 		items.value.forEach((_el, idx) => {
	// 			addGoods(idx);
	// 		});
	// 	}
	// 	if (!newV && oldV) {
	// 		items.value.forEach((el) => {
	// 			el.goods = [];
	// 		});
	// 	}
	// 	resultCalc();
	// });

	const changeGoods = (itemIdx: number, goodIdx: number, values: IClearanceItem) => {
		if (items.value[itemIdx].goods?.[goodIdx]) {
			items.value[itemIdx].goods[goodIdx] = values;
		}
	};

	const removeGoods = (itemIdx: number, goodIdx: number) => {
		items.value[itemIdx].goods?.splice(goodIdx, 1);
	};

	const addFile = (file: File) => {
		const url = URL.createObjectURL(file);

		files.value.push({
			file: file,
			url: url,
		});
	};

	const removeFile = (idx: number) => {
		files.value = files.value.filter((item, index) => {
			if (index !== idx) return item;
		});
	};

	const resultCalc = async () => {
		const cargos = items.value.map((el) => {
			return {
				...el,
				quantity: el.count,
				count: undefined,
			};
		});
		const localItem = {
			type: 'avia',
			cargos: cargos,
			isClearance: isClearance.value,
			isInsure: isInsure.value,
		};

		try {
			const res = await $fetch<CalcObject>('/calculate_price/', {
				baseURL: runtimeConfig.public.CARGO_API_URL,
				method: 'post',
				body: localItem,
			});

			results.value = res;

			postCalcOtherType(localItem);
		} catch (error: unknown) {
			console.log(error);
			// toast.showError('Калькуляция не обновилась');
		}
	};

	async function postCalcOtherType(payload: CalcPayload) {
		try {
			autoRes.value = undefined;
			trainRes.value = undefined;

			const [train, auto] = await Promise.allSettled(
				['train', 'auto'].map((delT) => {
					return $fetch<CalcObject>('/calculate_price/', {
						baseURL: runtimeConfig.public.CARGO_API_URL,
						method: 'POST',
						body: {
							...payload,
							type: delT,
						},
					});
				})
			);

			autoRes.value = auto.status === 'fulfilled' ? auto.value : undefined;

			trainRes.value = train.status === 'fulfilled' ? train.value : undefined;
		} catch (e) {
			console.error(e);
		}
	}

	const deleteItemFromArray = async (idx: number) => {
		items.value.splice(idx, 1);
		await resultCalc();
	};

	const changeItemFromArray = async () => {
		await resultCalc();
	};

	watch(isInsure, async () => {
		await resultCalc();
	});

	watch(
		[isInsure, items],
		() => {
			calcStorage.saveValues(isInsure.value, items.value);
		},
		{ deep: true }
	);

	const prepareSubmit = async () => {
		const hasErrors = checkDimensionsError();
		if (!hasErrors) {
			orderPayload.value = {
				type: 'avia',
				isClearance: isClearance.value,
				isInsure: isInsure.value,
				cargos: items.value,
			};
			isActiveModal.value = true;
		} else {
			toast.showError('Заполните обязательные поля');
		}
	};

	const onOrderSubmitted = async (orderId: number | undefined) => {
		try {
			if (orderId && files.value.length) {
				const formData = new FormData();
				files.value.forEach((f) => {
					formData.append('files', f.file);
				});
				await $fetch(`/api/local/order/${orderId}/addFiles`, {
					baseURL: runtimeConfig.public.API_URL,
					method: 'POST',
					body: formData,
				});
			}
		} catch (error) {
			console.log(error);
			toast.showError('Файлы не загрузились');
		} finally {
			isActiveModal.value = false;
		}
	};

	provide<{ addGoods: (itemIdx: number) => void }>('addGoods', {
		addGoods,
	});

	provide<{ changeGoods: (itemIdx: number, goodIdx: number, values: IClearanceItem) => void }>(
		'changeGoods',
		{
			changeGoods,
		}
	);

	provide<{ removeGoods: (itemIdx: number, goodIdx: number) => void }>('removeGoods', {
		removeGoods,
	});

	provide<{ addFile: (file: File) => void }>('addFile', {
		addFile,
	});

	provide<{ removeFile: (idx: number) => void }>('removeFile', {
		removeFile,
	});

	function getCargoDetailedString(cargoDetail: CargosDetailed, idx: number) {
		const totalWord = getWordByNumber(cargoDetail?.quantity, ['место', 'места', 'мест']);
		return `объем ${cargoDetail?.volume} м³, вес ${formatNumber(items.value?.[idx]?.weight || 0)} кг, ${
			cargoDetail?.quantity
		} ${totalWord}`;
	}

	onMounted(async () => {
		const storageValues = calcStorage.getValues();

		if (storageValues !== null) {
			items.value = storageValues.items;
			isInsure.value = storageValues.isInsure ?? false;
		}

		await calcStorage.resetQuery();
		await resultCalc();
	});
</script>

<template>
	<section id="calc" class="calculator-section">
		<h2 class="calculator-section__title">Калькулятор для расчёта стоимости</h2>
		<div class="calculator-section__description">
			Введите данные и определите стоимость транспортировки Ваших грузов
		</div>
		<form class="calculator-section__form-container">
			<TransitionGroup name="dimensions-card">
				<BlockOfFormDimensions
					v-for="(_, idx) in items"
					ref="blockOfFormDimensions"
					:key="idx"
					v-model:item="items[idx]"
					:idx="idx"
					:can-delete="isDelete"
					@update:item="changeItemFromArray"
					@delete-cargo-item="deleteItemFromArray(idx)"
				/>
			</TransitionGroup>

			<button type="button" class="form-container__add-cargo form-container__card" @click="addItem">
				<div class="add-icon__container">
					<Icon name="local:plus" class="add-icon" />
				</div>
				<div>Добавить груз</div>
			</button>

			<!-- <div class="form-container__card customs-clearance__card">
				<UiInputSwitch v-model="isClearance" name="isClearance">
					<template #label>
						<span class="form-container__card-title">Таможенное оформление</span>
					</template>
				</UiInputSwitch>
				<div v-if="isClearance" class="cargo-data__list">
					<TransitionGroup name="dimensions-card">
						<BlockOfFormCargoData
							v-for="(item, idx) in items"
							ref="blockOfFormCargoGoods"
							:key="idx"
							:idx="idx"
							:items="item.goods as IClearanceItem[]"
						/>
					</TransitionGroup>
				</div>
			</div> -->

			<UiInputSwitch v-model="isInsure" name="isInsureCargo">
				<template #label>
					<div>
						<span class="form-container__card-title">Страхование груза</span>
						<span class="switch__label-description">
							(итоговая стоимость увеличивается на 2%)
						</span>
					</div>
				</template>
			</UiInputSwitch>

			<div class="form-container__card">
				<div class="form-container__card-title">Загрузить файл</div>
				<FileContainer class="calc__files" :files="files" />
			</div>

			<button type="button" class="button calculator-section__button" @click="prepareSubmit">
				Рассчитать стоимость
			</button>
		</form>

		<AppModal v-if="isActiveModal" v-model:is-open="isActiveModal" modal-title="Оставить заявку">
			<FormOrderRequest
				:order-payload="orderPayload"
				@submit-form="onOrderSubmitted"
				@close-form="isActiveModal = false"
			/>
		</AppModal>
	</section>
</template>

<style lang="scss" scoped>
	.calculator-section__title {
		font-size: var(--font-size-title);
		color: var(--color-tertiary);
		font-weight: 700;
		margin-bottom: 12px;
	}
	.calculator-section__description {
		margin-bottom: 18px;
	}
	.calculator-section__form-container {
		display: grid;
		grid-template-columns: 1fr;
		gap: 12px;
	}
	.form-container__card {
		background-color: var(--color-white);
		padding: 20px 15px 15px;
		box-shadow: var(--box-shadow);
		border-radius: 20px;
	}
	.form-container__card-title {
		font-size: 20px;
		font-weight: 700;
	}
	.form-container__add-cargo {
		cursor: pointer;
		display: flex;
		align-items: center;
		padding: 12px 15px;
		font-size: 20px;
		font-weight: 700;
		transition: color ease-in-out 0.2s;
		&:hover,
		&:focus-visible {
			color: var(--color-secondary);
		}
		.add-icon__container {
			display: flex;
			align-items: center;
			justify-content: center;
			height: 42px;
			width: 42px;
			border-radius: 50%;
			margin-right: 10px;
			background-color: var(--color-primary);
		}
		.add-icon {
			color: var(--color-white);
			width: 36px;
			height: 36px;
		}
	}

	.switch__label-description {
		font-size: 20px;
		font-weight: 700;
		color: var(--color-grey-dark);
	}

	.customs-clearance__card {
		display: grid;
		gap: 20px;
	}

	.cargo-data__list {
		display: flex;
		flex-direction: column;
		gap: 40px;
	}

	.total-card {
		display: flex;
		flex-direction: column;
		gap: 8px;
		.total-card__details {
			display: flex;
			flex-direction: column;
			gap: 10px;
			padding-bottom: 10px;
			margin-top: 12px;
			border-bottom: 1px solid var(--color-grey-dark);
		}
		.total-card__details-item {
			display: flex;
			flex-wrap: wrap;
			justify-content: start;
			gap: 0 1em;
			.details-item__size {
				color: var(--color-grey-dark);
			}
			.details-item__price {
				flex: 1 0 auto;
				text-align: end;
			}
		}
		.total-card__item-insurance {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
		}
		.total-card__content {
			display: flex;
			justify-content: space-between;
		}
		.total-card__currency {
			font-weight: 700;
			font-size: 20px;
		}
		.total-card__date {
			color: var(--color-grey-dark);
			font-size: 16px;
			text-align: end;
		}
	}

	.calculator-section__button {
		height: 48px;
	}

	.dimensions-card-enter {
		position: relative;
		overflow: hidden;
	}
	.dimensions-card-enter-active {
		overflow: hidden;
		animation: slide-in 0.5s ease-out forwards;
	}

	.dimensions-card-leave {
		position: relative;
		overflow: hidden;
	}

	.dimensions-card-leave-active {
		animation: slide-out 0.5s ease-in forwards;
	}
	.dimensions-card-move {
		transition: all 0.5s;
	}

	@keyframes slide-in {
		from {
			transform: translateY(20px);
			opacity: 0;
			padding: 0 20px;
		}
		to {
			transform: translateY(0px);
			opacity: 1;
			padding: 20px 20px;
		}
	}

	@keyframes slide-out {
		from {
			transform: translateY(0px) scaleY(1);
			opacity: 1;
		}
		to {
			transform: translateY(-40px) scaleY(0.8);
			opacity: 0;
		}
	}

	@media (min-width: 1240px) {
		.calculator-section__title {
			margin-bottom: 15px;
		}
		.calculator-section__description {
			margin-bottom: 25px;
		}
		.calculator-section__form-container {
			gap: 20px;
		}
		.form-container__card {
			padding: 20px;
		}
		.form-container__card-title {
			font-size: 24px;
		}
		.form-container__add-cargo {
			font-size: 24px;
			padding: 20px;
			.add-icon__container {
				height: 31px;
				width: 31px;
				margin-right: 12px;
			}
			.add-icon {
				color: var(--color-white);
				width: 21px;
				height: 21px;
			}
		}

		.switch__label-description {
			font-size: 24px;
		}

		.customs-clearance__card {
			display: grid;
			gap: 20px;
		}

		.total-card {
			gap: 10px;
			.total-card__currency {
				font-size: 24px;
			}
			.total-card__date {
				font-size: var(--font-size-text);
			}
		}

		.calculator-section__button {
			height: 42px;
		}

		.dimensions-card-enter {
			position: relative;
			overflow: hidden;
		}
		.dimensions-card-enter-active {
			overflow: hidden;
			animation: slide-in 0.5s ease-out forwards;
		}

		.dimensions-card-leave {
			position: relative;
			overflow: hidden;
		}

		.dimensions-card-leave-active {
			animation: slide-out 0.5s ease-in forwards;
		}
		.dimensions-card-move {
			transition: all 0.5s;
		}
	}
</style>
